import { apiFetchJson } from '../http/apiFetch';
import type { ApiActivityLog, ApiActivityFilters, PagedApiActivity } from '../../models/apiActivity';

function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value);
}

function asOptionalString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  return str === '' ? null : str;
}

function asNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return String(value ?? '').trim().toLowerCase() === 'true';
}

export function normalizeApiActivityLog(row: unknown): ApiActivityLog | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const a = row as Record<string, unknown>;
  const id = asString(a.id ?? a.Id).trim();
  if (!id) {
    return null;
  }
  return {
    id,
    companyId: asOptionalString(a.companyId ?? a.CompanyId),
    merchantId: asOptionalString(a.merchantId ?? a.MerchantId),
    userId: asOptionalString(a.userId ?? a.UserId),
    route: asString(a.route ?? a.Route).trim(),
    httpMethod: asString(a.httpMethod ?? a.HttpMethod).trim(),
    statusCode: asNumber(a.statusCode ?? a.StatusCode),
    durationMs: asNumber(a.durationMs ?? a.DurationMs),
    responseSizeBytes: asNumber(a.responseSizeBytes ?? a.ResponseSizeBytes),
    ipAddress: asOptionalString(a.ipAddress ?? a.IpAddress),
    correlationId: asOptionalString(a.correlationId ?? a.CorrelationId),
    authScheme: asString(a.authScheme ?? a.AuthScheme).trim(),
    sensitive: asBoolean(a.sensitive ?? a.Sensitive),
    createdAt: asString(a.createdAt ?? a.CreatedAt),
  };
}

function normalizePagedApiActivity(result: unknown): PagedApiActivity {
  if (!result || typeof result !== 'object') {
    return { items: [], totalCount: 0, page: 1, pageSize: 0 };
  }
  const r = result as Record<string, unknown>;
  const rawItems = r.items ?? r.Items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(normalizeApiActivityLog).filter((a): a is ApiActivityLog => Boolean(a))
    : [];
  return {
    items,
    totalCount: asNumber(r.totalCount ?? r.TotalCount),
    page: asNumber(r.page ?? r.Page),
    pageSize: asNumber(r.pageSize ?? r.PageSize),
  };
}

export async function fetchApiActivity(
  getAccessToken: () => string | undefined,
  filters: ApiActivityFilters,
  page: number,
  pageSize: number
): Promise<PagedApiActivity> {
  const params = new URLSearchParams();
  if (filters.route?.trim()) {
    params.set('route', filters.route.trim());
  }
  if (filters.userId?.trim()) {
    params.set('userId', filters.userId.trim());
  }
  if (filters.companyId?.trim()) {
    params.set('companyId', filters.companyId.trim());
  }
  if (filters.statusCode?.trim()) {
    params.set('statusCode', filters.statusCode.trim());
  }
  if (filters.fromDate?.trim()) {
    params.set('fromUtc', filters.fromDate.trim());
  }
  if (filters.toDate?.trim()) {
    params.set('toUtc', filters.toDate.trim());
  }
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  const result = await apiFetchJson<unknown>(`/api/admin/security/api-activity?${params.toString()}`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });

  return normalizePagedApiActivity(result);
}
